import { faDiscord } from "@fortawesome/free-brands-svg-icons";
import {
  faArrowRightFromBracket,
  faCircleQuestion,
  faCreditCardFront,
  faEllipsis,
  faMessageLines,
  faPlus,
  faRectanglePro,
  faSparkles,
} from "@fortawesome/pro-regular-svg-icons";
import { faChartColumn, faUser } from "@fortawesome/pro-solid-svg-icons";
import {
  faGear,
  faSquareSlidersVertical,
  faSquareUser,
} from "@fortawesome/sharp-solid-svg-icons";
import { Box, Stack, SxProps, TooltipProps } from "@mui/material";
import { forwardRef, useMemo } from "react";

import { NewBadge } from "@/components/NewBadge";
import {
  Avatar,
  bindMenu,
  bindMenuTrigger,
  Divider,
  Icon,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMenuState,
  useTheme,
} from "@/components/ui-v2";
import { useTrackNextAppSwitch } from "@/hooks";
import { discordUrl, supportUrl } from "@/utils/constants";
import { getWebAppLoginUrl } from "@/utils/env";

import { useWorkspaceUnitBalance } from "./useWorkspaceUnitBalance";
import { WorkspaceDropdownButton } from "./WorkspaceDropdownButton";
import { WorkspaceDropdownUnitsProgress } from "./WorkspaceDropdownUnitsProgress";

interface WorkspaceDropdownProps {
  allWorkspaces: WorkspaceDropdownInfo[];
  currentWorkspaceId: string;
  userEmail: string;
  userFullName?: string;
  userProfilePictureUrl?: string;
  handleIntercomMessage?: () => void;
  startOpen?: boolean;
}

import type { WorkspaceDropdownInfo } from "./types";

export const getWorkspaceDisplayName = (workspace: WorkspaceDropdownInfo) => {
  return workspace.displayName || workspace.name;
};

const getWorkspaceInitial = (workspace: WorkspaceDropdownInfo) => {
  return (getWorkspaceDisplayName(workspace) || "w").charAt(0).toUpperCase();
};

interface WorkspaceAvatarProps {
  workspace: WorkspaceDropdownInfo;
  sx: SxProps;
  isLink?: boolean;
  onClick?: (e?: any) => void;
  tooltipPlacement?: TooltipProps["placement"];
  disableTooltip?: TooltipProps["disableHoverListener"];
}

export const WorkspaceAvatar = forwardRef<
  HTMLAnchorElement,
  WorkspaceAvatarProps
>(
  (
    {
      workspace,
      sx,
      onClick,
      isLink = true,
      tooltipPlacement = "top",
      disableTooltip = false,
    }: WorkspaceAvatarProps,
    ref,
  ) => {
    const linkProps = {
      component: Link,
      href: `/${workspace.name}`,
    };

    return (
      <Tooltip
        title={workspace.displayName}
        placement={tooltipPlacement}
        arrow
        enterDelay={300}
        disableHoverListener={disableTooltip}
        disableFocusListener={disableTooltip}
      >
        <Avatar
          ref={ref}
          component="span"
          {...(isLink && linkProps)}
          onClick={onClick}
          sx={{
            color: "primary.dark",
            bgcolor: workspace.profilePicture ? "transparent" : "primary.light",
            fontWeight: "bold",
            borderRadius: 2,
            "&&&": {
              border: "none",
            },
            ...sx,
          }}
          src={workspace.profilePicture}
          alt={getWorkspaceDisplayName(workspace)}
        >
          {getWorkspaceInitial(workspace)}
        </Avatar>
      </Tooltip>
    );
  },
);

export const WorkspaceDropdown = (props: WorkspaceDropdownProps) => {
  const {
    allWorkspaces,
    currentWorkspaceId,
    userEmail,
    userFullName,
    userProfilePictureUrl,
    handleIntercomMessage,
    startOpen = false,
  } = props;

  const theme = useTheme();
  const { palette } = theme;
  const { percentageRemaining, remainingUnits } = useWorkspaceUnitBalance();

  const workspaces = useMemo(
    () =>
      allWorkspaces.reduce(
        (acc, workspace) => {
          if (workspace.id === currentWorkspaceId) {
            acc.current = workspace;
          } else {
            acc.nonCurrent.push(workspace);
          }
          acc.all.push(workspace);
          return acc;
        },
        {
          current: null as WorkspaceDropdownInfo | null,
          nonCurrent: [] as WorkspaceDropdownInfo[],
          all: [] as WorkspaceDropdownInfo[],
        },
      ),
    [allWorkspaces, currentWorkspaceId],
  );

  const trackNextAppSwitch = useTrackNextAppSwitch();

  const mainMenuState = useMenuState({ startOpen });

  const handleTryNextApp = () => {
    trackNextAppSwitch("workspace_menu");
    mainMenuState.onClose();
  };
  const workspacesMenuState = useMenuState({
    parentMenuState: mainMenuState,
    openOnHover: true,
    closeOnLeave: true,
  });
  const billingMenuState = useMenuState({
    parentMenuState: mainMenuState,
    openOnHover: true,
    closeOnLeave: true,
  });

  if (!workspaces.current) {
    return null;
  }

  return (
    <>
      <WorkspaceDropdownButton
        mainMenuState={mainMenuState}
        workspaces={workspaces}
        percentageRemaining={percentageRemaining}
        remainingUnits={remainingUnits}
      />
      <Menu
        {...bindMenu(mainMenuState)}
        MenuListProps={{
          component: "nav",
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: 0 }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          "& .MuiPaper-root": {
            boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.20)",
            backgroundColor: palette.secondary[7],
            border: `1px solid ${palette.secondary[6]}`,
            width: "204px",
            minWidth: "204px",
            borderRadius: "4px",
            marginTop: 1,
            marginLeft: -2,
          },

          "& .MuiList-root": {
            paddingTop: 0,
            paddingBottom: 1,
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: palette.secondary[6],
            borderRadius: "4px",
            borderBottom: `1px solid ${palette.secondary[6]}`,
            padding: 0,
            margin: 0,
            paddingBottom: 1,
            marginBottom: 2,
            boxSizing: "content-box",
          }}
        >
          <Stack direction="column" spacing={2.5} sx={{ p: 3.5, pb: 3 }}>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: "start",
              }}
            >
              <WorkspaceAvatar
                workspace={workspaces.current}
                sx={{
                  width: "72px",
                  height: "72px",
                  pointerEvents: "none",
                  borderRadius: "4px",
                  flexShrink: 0,
                  fontSize: 32,
                  transform: "translate(-4px, -4px)",
                }}
              />

              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "center",
                  justifyContent: "end",
                  flexGrow: 1,
                  transform: "translate(2px, -4px)",
                }}
              >
                {workspaces.all.slice(0, 2).map((workspace) => (
                  <Tooltip
                    placement="top"
                    key={workspace.id}
                    title={`Switch to ${workspace.displayName}`}
                    arrow
                  >
                    <div>
                      <WorkspaceAvatar
                        disableTooltip
                        onClick={mainMenuState.onClose}
                        workspace={workspace}
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 1.5,
                          fontSize: 12,
                        }}
                      />
                    </div>
                  </Tooltip>
                ))}

                <IconButton
                  size="small"
                  variant="secondary"
                  aria-label="Switch Workspace"
                  sx={{
                    minWidth: 20,
                    minHeight: 20,
                    width: 20,
                    height: 20,
                    borderRadius: 1.5,
                    padding: 0,
                    border: "none",
                    fontSize: 13,
                    boxShadow: workspacesMenuState.open
                      ? `inset 0 0 0 0.5px ${palette.secondary["3"]}`
                      : `inset 0 0 0 0.5px ${palette.secondary["4"]}`,
                    color: workspacesMenuState.open
                      ? "secondary.1"
                      : "secondary.2",
                    backgroundColor: workspacesMenuState.open
                      ? "secondary.5"
                      : "transparent",
                  }}
                  {...bindMenuTrigger(workspacesMenuState)}
                >
                  <Icon icon={faEllipsis} />
                </IconButton>
              </Stack>
            </Stack>
            <Stack direction="column" spacing={2}>
              <Typography
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                fontWeight={600}
                variant="regular"
                lineHeight={1}
              >
                {getWorkspaceDisplayName(workspaces.current)}
              </Typography>
            </Stack>
            <WorkspaceDropdownUnitsProgress
              percentageRemaining={percentageRemaining}
            />
          </Stack>

          {workspaces.current.myPermissions?.includes("workspace:settings") && (
            <MenuItem
              component={Link}
              href={`/${workspaces.current.name}/settings/general`}
              onClick={mainMenuState.onClose}
              icon={<Icon icon={faSquareSlidersVertical} />}
              variant="primary"
            >
              Workspace Settings
            </MenuItem>
          )}
          {workspaces.current.myPermissions?.includes("workspace:members") && (
            <MenuItem
              component={Link}
              href={`/${workspaces.current.name}/settings/members`}
              onClick={mainMenuState.onClose}
              icon={<Icon icon={faSquareUser} />}
              variant="primary"
            >
              Members
            </MenuItem>
          )}
          {workspaces.current.myPermissions?.includes(
            "workspace:billing:admin",
          ) && (
            <MenuItem
              component={Link}
              href={`/${workspaces.current?.name}/settings/usage`}
              onClick={mainMenuState.onClose}
              icon={<Icon icon={faChartColumn} />}
              variant="primary"
            >
              Billing & Usage
            </MenuItem>
          )}
        </Box>
        <MenuItem
          component={Link}
          href={getWebAppLoginUrl()}
          onClick={handleTryNextApp}
          icon={<Icon icon={faSparkles} />}
          variant="primary"
        >
          <Stack direction="row" sx={{ alignItems: "center", gap: 1.5 }}>
            <span>Try the new Layer</span>
            <NewBadge />
          </Stack>
        </MenuItem>
        <MenuItem
          component={Link}
          href={supportUrl}
          target="_blank"
          onClick={mainMenuState.onClose}
          icon={<Icon icon={faCircleQuestion} />}
          sx={{
            color: "text.secondary",
            paddingRight: theme.spacing(8),
            fontWeight: 500,
          }}
        >
          Layer Docs & FAQs
        </MenuItem>
        <MenuItem
          icon={<Icon icon={faMessageLines} />}
          onClick={() => {
            if (handleIntercomMessage) {
              handleIntercomMessage();
            }

            mainMenuState.onClose();
          }}
          sx={{
            color: "text.secondary",
            paddingRight: theme.spacing(8),
            fontWeight: 500,
          }}
        >
          Contact Support
        </MenuItem>
        <Divider />
        <MenuItem
          component={Link}
          href={discordUrl}
          target="_blank"
          onClick={mainMenuState.onClose}
          icon={
            <Icon icon={faDiscord} style={{ transform: "translateX(-1px)" }} />
          }
        >
          Join our Discord
        </MenuItem>
        <Divider />
        <Stack direction="column" spacing={1} sx={{ px: 3, py: 1 }}>
          <Typography variant="micro" color="text.secondary" fontWeight="light">
            Signed in as
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
              }}
            >
              <Avatar
                sx={{
                  width: "16px",
                  height: "16px",
                  fontSize: "16px",
                  backgroundColor: "primary.light",
                  color: "primary.dark",
                }}
                src={userProfilePictureUrl}
                alt={userFullName || userEmail}
              />
            </Box>
            <Stack
              direction="column"
              spacing={1}
              sx={{
                overflow: "hidden",
              }}
            >
              <Typography
                variant="small"
                color="text.primary"
                lineHeight={1}
                noWrap
              >
                {userFullName || userEmail}
              </Typography>
              <Typography
                variant="mini"
                color="text.secondary"
                lineHeight={1}
                noWrap
              >
                {userEmail}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        {workspaces.current.myPermissions?.includes("workspace:settings") && [
          <MenuItem
            key="user-settings"
            component={Link}
            href="/settings"
            icon={<Icon icon={faUser} />}
            onClick={mainMenuState.onClose}
            sx={{
              color: "text.primary",
              paddingRight: theme.spacing(8),
              fontWeight: 500,
            }}
          >
            User Settings
          </MenuItem>,
          <Divider key="divider" />,
        ]}
        <MenuItem
          component={Link}
          href="/logout"
          icon={
            <Icon
              icon={faArrowRightFromBracket}
              style={{ transform: "translateX(2px)" }}
            />
          }
        >
          Log Out
        </MenuItem>
      </Menu>
      <Menu
        {...bindMenu(billingMenuState)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          "& .MuiPaper-root": {
            marginLeft: 3.5,
            marginTop: -2.5,
          },
        }}
      >
        <MenuItem
          component={Link}
          href={`/${workspaces.current?.name}/settings/subscription/plan`}
          onClick={mainMenuState.onClose}
          icon={<Icon icon={faRectanglePro} />}
        >
          Manage Plan
        </MenuItem>
        <MenuItem
          component={Link}
          href={`/${workspaces.current?.name}/settings/usage/subscription`}
          onClick={mainMenuState.onClose}
          icon={<Icon icon={faCreditCardFront} />}
        >
          Billing & Payment
        </MenuItem>
      </Menu>
      <Menu
        {...bindMenu(workspacesMenuState)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          "& .MuiPaper-root": {
            width: "210px",
            marginLeft: 8.5,
            marginTop: -2.75,
          },
        }}
      >
        <Box
          sx={{
            padding: "0px 8px 8px 8px",
            borderBottom: `1px solid ${palette.secondary[6]}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="mini" color="text.secondary" fontWeight={600}>
            Switch Workspace
          </Typography>

          <IconButton
            size="small"
            variant="secondary"
            aria-label="Manage Memberships"
            component={Link}
            href="/settings/general"
            onClick={mainMenuState.onClose}
            sx={{
              minWidth: 24,
              minHeight: 24,
              borderRadius: 1.5,
              padding: 0,
              border: "none",
              color: "secondary.2",
              backgroundColor: "transparent",

              "&:hover": {
                color: "secondary.1",
              },
            }}
          >
            <Icon icon={faGear} size="medium" />
          </IconButton>
        </Box>
        {workspaces.all.length > 0 && (
          <Box
            sx={{
              padding: 2,
            }}
          >
            <Typography variant="micro" color="text.secondary" fontWeight={600}>
              Workspaces
            </Typography>
          </Box>
        )}
        {workspaces.all.map((workspace) => (
          <MenuItem
            key={workspace.id}
            component={Link}
            href={`/${workspace.name}`}
            onClick={mainMenuState.onClose}
            selected={workspaces.current?.id === workspace.id}
            icon={
              <WorkspaceAvatar
                isLink={false}
                workspace={workspace}
                sx={{
                  width: 16,
                  height: 16,
                  fontSize: 10,
                  borderRadius: 1,
                }}
                onClick={mainMenuState.onClose}
              />
            }
          >
            {getWorkspaceDisplayName(workspace)}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          component={Link}
          href="/new"
          onClick={mainMenuState.onClose}
          icon={<Icon icon={faPlus} />}
        >
          Create New Workspace
        </MenuItem>
      </Menu>
    </>
  );
};
